import React from 'react';
import { Menu, Bell, User, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { organizationsApi, versionsApi, projectsApi, meApi, MeContextResponse } from '../services/api';
import { Organization, AUTVersion, Project } from '../types';
import BrandName from './BrandName';

interface HeaderProps {
  onMenuToggle: () => void;
  sidebarCollapsed: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle, sidebarCollapsed }) => {
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<AUTVersion | null>(null);
  const [meContext, setMeContext] = useState<MeContextResponse | null>(null);
  const { user, loading, signOut } = useAuth();

  useEffect(() => {
    if (loading || !user) return;

    const load = async () => {
      try {
        const ctx = await meApi.getContext();
        setMeContext(ctx);
        if (ctx.default_project) {
          localStorage.setItem('selectedOrganizationId', String(ctx.organization_id));
          localStorage.setItem('selectedProjectId', String(ctx.default_project.id));
          window.dispatchEvent(new Event('organizationChanged'));
          window.dispatchEvent(new Event('projectChanged'));
        }
      } catch {
        setMeContext(null);
      }
    };

    const timer = setTimeout(() => {
      load();
    }, 100);

    const handleOrgChange = () => loadCurrentContext();
    const handleVersionChange = () => loadCurrentContext();
    const handleProjectChange = () => loadCurrentContext();
    const handleMeContextChange = () => load();

    window.addEventListener('organizationChanged', handleOrgChange);
    window.addEventListener('versionChanged', handleVersionChange);
    window.addEventListener('projectChanged', handleProjectChange);
    window.addEventListener('meContextChanged', handleMeContextChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('organizationChanged', handleOrgChange);
      window.removeEventListener('versionChanged', handleVersionChange);
      window.removeEventListener('projectChanged', handleProjectChange);
      window.removeEventListener('meContextChanged', handleMeContextChange);
    };
  }, [loading, user]);

  useEffect(() => {
    if (loading || !user) return;
    const timer = setTimeout(() => loadCurrentContext(), 200);
    return () => clearTimeout(timer);
  }, [loading, user, meContext]);

  const loadCurrentContext = async () => {
    try {
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const versionIdStr = localStorage.getItem('selectedVersionId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      
      if (orgIdStr) {
        const orgId = parseInt(orgIdStr);
        
        // Retry organization list call with exponential backoff
        let organizations: Organization[] = [];
        try {
          organizations = await organizationsApi.list();
        } catch (error: any) {
          // Retry once after a delay if it's a network error
          if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED') {
            await new Promise(resolve => setTimeout(resolve, 1000));
            try {
              organizations = await organizationsApi.list();
            } catch (retryError) {
              console.error('Failed to load organizations after retry:', retryError);
              return; // Fail silently if retry also fails
            }
          } else {
            throw error;
          }
        }
        
        const org = organizations.find(o => o.id === orgId);
        setSelectedOrg(org || null);
        
        if (projectIdStr) {
          try {
            const project = await projectsApi.get(orgId, parseInt(projectIdStr));
            setSelectedProject(project);
          } catch {
            setSelectedProject(null);
          }
        } else {
          setSelectedProject(null);
        }

        if (versionIdStr && org && projectIdStr) {
          try {
            const versionId = parseInt(versionIdStr);
            const versions = await versionsApi.list(orgId, parseInt(projectIdStr));
            const version = versions.find(v => v.id === versionId);
            setSelectedVersion(version || null);
          } catch {
            setSelectedVersion(null);
          }
        } else {
          setSelectedVersion(null);
        }
      } else {
        setSelectedOrg(null);
        setSelectedProject(null);
        setSelectedVersion(null);
      }
    } catch (error) {
      console.error('Failed to load current context:', error);
      setSelectedOrg(null);
      setSelectedVersion(null);
    }
  };
  return (
    <header className="bg-slate-900 shadow-sm border-b border-slate-700">
      <div className="flex items-center justify-between h-16 px-6">
        {/* Left side */}
        <div className="flex items-center">
          <button
            onClick={onMenuToggle}
            className="p-2 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors mr-4"
          >
            <Menu size={20} />
          </button>
          
          {sidebarCollapsed && (
            <h1 className="text-xl font-bold">
              <BrandName size="md" />
            </h1>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          <button className="p-2 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors relative"
            aria-label="Notifications"
          >
            <Bell size={20} />
            <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-slate-900"></span>
          </button>

          {/* User menu: Organization | Default project, then email */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 px-3 py-2 rounded-md bg-slate-800/50 text-slate-200 border border-slate-700">
              <User size={18} className="text-slate-400" />
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-medium text-slate-100">
                  {meContext
                    ? `${meContext.organization_name}${meContext.default_project ? ` | ${meContext.default_project.name}` : ''}`
                    : (selectedOrg && selectedProject)
                    ? `${selectedOrg.name} | ${selectedProject.name}`
                    : selectedOrg
                    ? selectedOrg.name
                    : user?.email || user?.username || 'Signed out'}
                </span>
                <span className="text-xs text-slate-400">
                  {user?.email || user?.username}
                </span>
              </div>
            </div>
            <button
              onClick={signOut}
              className="inline-flex items-center space-x-2 px-3 py-2 rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700 transition-colors"
            >
              <LogOut size={16} />
              <span className="text-sm font-semibold">Sign out</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
